/**
 * INTERNAL engine plugins: markdown-style input rules, the empty-doc placeholder decoration, and
 * the provisional-stamp plugin that makes fresh local typing visibly provisional (the design's
 * load-bearing rule, AC 3.2). Assembled — in a fixed, order-sensitive stack — by `EditorCore`.
 * PM types stay inside this file; nothing here is re-exported from `index.ts`.
 */

import { Plugin, type EditorState, type Transaction } from 'prosemirror-state';
import { Decoration, DecorationSet } from 'prosemirror-view';
import { InputRule, inputRules, textblockTypeInputRule, wrappingInputRule } from 'prosemirror-inputrules';
import { ySyncPluginKey } from 'y-prosemirror';
import { editorSchema } from '../schema/schema';
import { COMMITMENT_MARK } from '../schema/marks/commitment';
import { ATTRIBUTION_MARK } from '../schema/marks/attribution';
import type { CommitmentState, ParticipantRef } from '../types';

/** Transaction meta set by the engine's own write paths so the stamp plugin skips them. */
export const DOCIT_INTERNAL = 'docit-internal';

/** Markdown-style typing shortcuts over the v2 schema (headings, lists, quote, code, hr). */
export function buildInputRules(): Plugin {
  const { heading, bullet_list: bulletList, ordered_list: orderedList, blockquote, code_block: codeBlock, hr } = editorSchema.nodes;
  return inputRules({
    rules: [
      // '#'…'####' + space → heading 1..4.
      textblockTypeInputRule(/^(#{1,4})\s$/, heading, (m) => ({ level: m[1].length })),
      // '- ' / '* ' / '+ ' → bullet list.
      wrappingInputRule(/^\s*([-+*])\s$/, bulletList),
      // '1. ' → ordered list, joining onto a preceding list when the number continues it.
      wrappingInputRule(
        /^(\d+)\.\s$/,
        orderedList,
        (m) => ({ order: Number(m[1]) }),
        (m, node) => node.childCount + (node.attrs.order as number) === Number(m[1]),
      ),
      // '> ' → blockquote.
      wrappingInputRule(/^\s*>\s$/, blockquote),
      // Triple backtick → code block.
      textblockTypeInputRule(/^```$/, codeBlock),
      // '---' (or *** / ___) alone on a line → horizontal rule, replacing the line.
      new InputRule(/^(?:---|\*\*\*|___)$/, (state: EditorState, match: RegExpMatchArray, start: number, end: number): Transaction | null =>
        state.tr.replaceRangeWith(start, end, hr.create({ markup: match[0] }))),
    ],
  });
}

/** Empty-doc placeholder: a node decoration on the single empty paragraph; CSS renders the text. */
export function placeholderPlugin(text: string): Plugin {
  return new Plugin({
    props: {
      decorations(state: EditorState): DecorationSet | null {
        const doc = state.doc;
        const first = doc.firstChild;
        if (doc.childCount === 1 && first && first.type.name === 'paragraph' && first.content.size === 0) {
          return DecorationSet.create(doc, [
            Decoration.node(0, first.nodeSize, { class: 'docit-placeholder', 'data-placeholder': text }),
          ]);
        }
        return null;
      },
    },
  });
}

/**
 * Stamp LOCAL content insertions as provisional + attributed to the local participant. Remote
 * (ySync) transactions and the engine's own attributed writes (meta `docit-internal`) are skipped —
 * they carry their own marks. The two marks are evaluated independently per inserted text node:
 * an inherited PROVISIONAL commitment mark is kept (typing at a provisional boundary stays that
 * span's state), but an inherited COMMITTED mark (the mark is inclusive:true) is REPLACED with a
 * fresh provisional one — otherwise typing inside/at the end of a committed span would silently
 * land as committed under the original commit's by/ts. Attribution is inclusive:false so it is
 * never inherited at the caret; it is stamped on every locally inserted run that lacks it.
 */
export function provisionalStampPlugin(localParticipant: ParticipantRef, now: () => string): Plugin {
  const commitType = editorSchema.marks[COMMITMENT_MARK];
  const attrType = editorSchema.marks[ATTRIBUTION_MARK];

  return new Plugin({
    appendTransaction(trs: readonly Transaction[], _oldState: EditorState, newState: EditorState): Transaction | null {
      // Collect the inserted ranges from local content transactions, mapped into newState.doc.
      const ranges: { from: number; to: number }[] = [];
      trs.forEach((tr, k) => {
        if (!tr.docChanged) return;
        if (tr.getMeta(ySyncPluginKey)) return; // remote sync transaction — attribution travels with it
        if (tr.getMeta(DOCIT_INTERNAL)) return; // engine's own attributed write — already marked
        tr.steps.forEach((step, i) => {
          step.getMap().forEach((_oldStart, _oldEnd, newStart, newEnd) => {
            let from = newStart;
            let to = newEnd;
            // Map through the remaining steps of THIS transaction…
            for (let j = i + 1; j < tr.steps.length; j += 1) {
              const m = tr.steps[j].getMap();
              from = m.map(from, 1);
              to = m.map(to, -1);
            }
            // …then through any later transactions in the batch.
            for (let j = k + 1; j < trs.length; j += 1) {
              from = trs[j].mapping.map(from, 1);
              to = trs[j].mapping.map(to, -1);
            }
            if (to > from) ranges.push({ from, to });
          });
        });
      });
      if (ranges.length === 0) return null;

      let out = newState.tr.setMeta(DOCIT_INTERNAL, true);
      let stamped = false;
      const max = newState.doc.content.size;
      for (const range of ranges) {
        const from = Math.max(0, Math.min(range.from, max));
        const to = Math.max(0, Math.min(range.to, max));
        if (to <= from) continue;
        newState.doc.nodesBetween(from, to, (node, pos) => {
          if (!node.isText) return true;
          const start = Math.max(from, pos);
          const end = Math.min(to, pos + node.nodeSize);
          if (end <= start) return false;
          // Keep an inherited PROVISIONAL mark; replace an inherited COMMITTED one (addMark of
          // the same MarkType replaces it) so fresh typing never carries false commit provenance.
          const inherited = commitType.isInSet(node.marks);
          if (!inherited || inherited.attrs.state === 'committed') {
            out = out.addMark(start, end, commitType.create({ state: 'provisional' as CommitmentState }));
            stamped = true;
          }
          // Attribution is never inherited (inclusive:false) — stamp any local run lacking it.
          if (!attrType.isInSet(node.marks)) {
            // Reuse the adjacent run's mark when it is THIS participant acting: a fresh
            // `ts` per keystroke makes every character its own text node (marks that differ
            // only by timestamp never merge), which grows the doc, the CRDT state, and the
            // DOM quadratically with typing — a few pages of prose froze the tab on open.
            // Same participant + act lane ⇒ same mark object ⇒ ProseMirror merges the run;
            // `ts` then means "when this run started", minted only at run boundaries.
            const $start = newState.doc.resolve(start);
            const before = $start.nodeBefore;
            const prevAttr = before && before.isText ? attrType.isInSet(before.marks) : null;
            const reusable =
              prevAttr &&
              prevAttr.attrs.participantId === localParticipant.id &&
              prevAttr.attrs.participantKind === localParticipant.kind &&
              prevAttr.attrs.lane === 'act'
                ? prevAttr
                : null;
            out = out.addMark(start, end, reusable ?? attrType.create({
              participantId: localParticipant.id,
              participantKind: localParticipant.kind,
              displayName: localParticipant.displayName,
              model: localParticipant.model ?? null,
              lane: 'act',
              ts: now(),
            }));
            stamped = true;
          }
          return false;
        });
      }
      return stamped ? out : null;
    },
  });
}
